/**
 * @fileoverview Client portal onboarding tour definitions.
 *
 * Each client portal route maps to an ordered list of guided-tour steps
 * (driver.js format). A step highlights an element and shows a popover; a
 * step with no `element` renders a centered popover (used for intros).
 *
 * Element targets are existing, stable structural class names on each page.
 * Targets that aren't present at runtime (empty states, collapsed panels,
 * conditional cards) are filtered out by the controller, so tours degrade
 * gracefully. For elements that are duplicated across responsive layouts
 * (e.g. desktop vs. mobile nav), `firstVisible(...)` resolves to whichever
 * copy is actually on screen.
 *
 * @see ./OnboardingProvider.js for how these are resolved and driven.
 */

/**
 * Returns a resolver that yields the first visible element matching any of
 * the given selectors, or null if none are visible. An element is treated
 * as visible when it has layout (offsetParent set, or non-zero rects).
 *
 * @param {...string} selectors - CSS selectors in priority order
 * @returns {() => (Element|null)} Resolver invoked at tour start
 */
export const firstVisible =
  (...selectors) =>
  () => {
    for (const selector of selectors) {
      const nodes = document.querySelectorAll(selector);
      for (const node of nodes) {
        const visible =
          node.offsetParent !== null || node.getClientRects().length > 0;
        if (visible) return node;
      }
    }
    return null;
  };

/**
 * Maps each route to the selector that signals the page has finished
 * loading its real content (past spinners/loading states). The controller
 * waits for this before auto-starting a tour.
 *
 * @constant {Object.<string, string>}
 */
export const READY_SELECTOR_BY_PATH = {
  "/dashboard": ".dashboard-grid",
  "/updates": ".page-header",
  "/selections": ".cs-tabs-nav",
  "/change-orders": ".page-header",
  "/documents": ".page-header",
  "/profile": ".profile-card-header",
};

/**
 * Maps each route to the userPreferences flag tracking tour completion.
 *
 * @constant {Object.<string, string>}
 */
export const SEEN_KEY_BY_PATH = {
  "/dashboard": "onboardingDashboardSeen",
  "/updates": "onboardingUpdatesSeen",
  "/selections": "onboardingSelectionsSeen",
  "/change-orders": "onboardingChangeOrdersSeen",
  "/documents": "onboardingDocumentsSeen",
  "/profile": "onboardingProfileSeen",
};

/** Highlights the main navigation (whichever layout is visible). */
const navStep = {
  element: firstVisible(".cn-desktop-links", ".cn-mobile-bottom"),
  popover: {
    title: "Getting around",
    description:
      "Use the main navigation to move between your Dashboard, Updates, Selections, Change Orders, and Documents.",
  },
};

/**
 * Final step on every page — highlights the profile menu so users know
 * where to replay a tour. Appended to each page's step list below, and
 * shown on its own when a user declines the opening tour prompt.
 */
export const replayStep = {
  element: firstVisible(".cn-profile-trigger", ".cn-mobile-profile-btn"),
  popover: {
    title: "Need a refresher?",
    description:
      "Open this menu anytime and choose <b>Take a tour</b> to replay the walkthrough for the page you're on.",
  },
};

/**
 * Tour steps keyed by route path.
 *
 * @constant {Object.<string, Array<Object>>}
 */
export const TOUR_STEPS = {
  "/dashboard": [
    {
      popover: {
        title: "Welcome to your home portal!",
        description:
          "This quick tour shows you around your home-building portal. You can stop anytime, and replay it later from the profile menu.",
      },
    },
    {
      element: ".dashboard-notification-center",
      popover: {
        title: "Notification Center",
        description:
          "Important alerts — new updates, documents, and deadlines — show up here. You can dismiss items once you've seen them.",
      },
    },
    {
      element: ".card-construction-phase",
      popover: {
        title: "Construction Phase",
        description:
          "See what stage your home is in right now, with a short summary of what's happening. Click <b>Learn More About This Phase</b> for more details.",
      },
    },
    {
      element: ".card-project-progress",
      popover: {
        title: "Project Progress",
        description:
          "A quick read on how far along your build is overall, based on construction phase.",
      },
    },
    {
      element: ".card-weekly-update",
      popover: {
        title: "Weekly Update",
        description:
          "A quick view of tasks and progress photos completed within the last 7 days will show here. Use the links to view everything on the Updates page.",
      },
    },
    navStep,
    replayStep,
  ],

  "/updates": [
    {
      popover: {
        title: "Project Updates",
        description:
          "This is your running log of construction progress. Your project manager will update this section throughout the week with tasks completed and photos from the jobsite.",
      },
    },
    {
      element: firstVisible(".updates-current-stats", ".page-header"),
      popover: {
        title: "This week at a glance",
        description:
          "A snapshot of tasks completed and photos added this week, as well as your project's current phase.",
      },
    },
    {
      element: firstVisible(".updates-section-title", ".updates-archive-week"),
      popover: {
        title: "Completed tasks",
        description:
          "Tasks your team finished — newest first — show here. Click a task to see details and any photos. Older weeks are tucked into an archive below.",
      },
    },
    {
      element: firstVisible(
        ".updates-photos-section",
        ".updates-photos-header",
      ),
      popover: {
        title: "Progress photos",
        description: "Browse all the progress photos for your home here.",
      },
    },
    {
      element: firstVisible(".updates-photos-toolbar"),
      popover: {
        title: "Filter & change the view",
        description:
          "You can group photos by week or construction phase, and switch between <b>LIST</b> (accordion-style) and <b>GALLERY</b> (all photos at once) views.",
      },
    },
    replayStep,
  ],

  "/selections": [
    {
      popover: {
        title: "Your Selections",
        description:
          "This is where you choose the finishes and materials for your home and track what still needs a decision.",
      },
    },
    {
      element: ".cs-deadline-alert",
      popover: {
        title: "Selections Deadline",
        description:
          "This is your 45-day countdown timer to complete your selections. After the deadline, selections will be locked and you will need to request a change order to make any updates.",
      },
    },
    {
      element: ".cs-tabs-nav",
      popover: {
        title: "Selection categories",
        description:
          "Switch between Overview, Paint Selections, Owner Purchases, and Documents using these tabs.",
      },
    },
    {
      element: firstVisible(".cs-section-header-row .cs-btn-add"),
      popover: {
        title: "Need a hand?",
        description:
          "Click <b>Instructions</b> for step-by-step guidance on completing your selections.",
      },
    },
    {
      element: firstVisible(".cs-edit-toggle-btn"),
      popover: {
        title: "Make your selections",
        description:
          "Click <b>Edit Selections</b> in any section to record your choices. A check mark means it's set; a warning icon means a selection is still needed. You can change your selections at any time before the deadline as long as the related item has not been ordered yet.",
      },
    },
    {
      element: firstVisible(".sp-actions-bar"),
      popover: {
        title: "Print or download",
        description:
          "Export a copy of your selections to print or save as a spreadsheet.",
      },
    },
    {
      element: firstVisible(".cs-vendor-section", ".cs-vendor-heading"),
      popover: {
        title: "Vendor information",
        description:
          "Contact these vendors to make selections. Don't purchase directly. They'll send us quotes and we'll confirm with you first.",
      },
    },
    replayStep,
  ],

  "/change-orders": [
    {
      popover: {
        title: "Change Orders",
        description:
          "Any approved changes to your project's scope, cost, or timeline are tracked here.",
      },
    },
    {
      element: firstVisible(".co-stats-section", ".page-header"),
      popover: {
        title: "Summary at a glance",
        description:
          "Totals for your change orders — how many, how many approved or pending, and their impact on cost and schedule.",
      },
    },
    {
      element: firstVisible(".co-list-section", ".co-section-title"),
      popover: {
        title: "Change order history",
        description:
          "Expand any change order to see its details, cost/time impact, and attached documents.",
      },
    },
    {
      element: firstVisible(".co-impact-section", ".co-impact-card"),
      popover: {
        title: "Impact summary",
        description:
          "Here you will find a running tally of how your approved change orders affect your project's total cost and timeline.",
      },
    },
    replayStep,
  ],

  "/documents": [
    {
      popover: {
        title: "Project Documents",
        description:
          "Your floor plans, site plans, and other project documents live here.",
      },
    },
    {
      element: firstVisible(
        ".document-category",
        ".documents-empty",
        ".page-header",
      ),
      popover: {
        title: "Browse by category",
        description:
          "Documents are grouped by type. Click any document to preview it, then download a copy if you need it.",
      },
    },
    replayStep,
  ],

  "/profile": [
    {
      popover: {
        title: "Your Profile",
        description:
          "Review your contact details and keep the info we use to reach you up to date.",
      },
    },
    {
      element: firstVisible(".profile-card-header"),
      popover: {
        title: "Your details",
        description: "Your name and the email you're signed in with.",
      },
    },
    {
      element: firstVisible(".profile-edit-btn", ".profile-actions"),
      popover: {
        title: "Editing your info",
        description:
          "Click <b>Update Information</b> to edit the fields you manage yourself, like your mailing address and secondary contact information. Other details are managed by your project manager — reach out if anything looks off.",
      },
    },
    replayStep,
  ],
};

export default TOUR_STEPS;
